/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { EntryRequest } from 'modules/teams/domain/interfaces/entry-request';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';

import { theme } from 'styles/theme/theme';
import { UDFilter, UDList, UDListItem, UDText } from 'ud-ui/components';
import { ParticipantsList } from '../participants/styles';

interface WaitingListProps {
  entryRequests: EntryRequest[];
}

/**
 * Список заявок только на чтение.
 *
 * Кнопки «Принять»/«Отклонить» дёргали мутацию `updateEntryRequest`, которой нет
 * ни в одной версии GraphQL-схемы начиная с самой первой, и REST-двойника у неё
 * тоже нет — то есть они всегда завершались ошибкой. Вернуть действия можно,
 * когда бэкенд отдаст ручку смены статуса заявки.
 */
const WaitingList = ({ entryRequests }: WaitingListProps) => {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');

  const entryRequestsByFilter = useMemo(() => {
    return entryRequests.filter((entryRequest) => {
      return getFullName(entryRequest.user).toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [entryRequests, filterValue]);

  return (
    <ParticipantsList>
      <UDFilter
        inputPlaceholder={t('participant.filter.inputPlaceholder')}
        value={filterValue}
        onChange={setFilterValue}
        style={{ marginBottom: 10 }}
      />
      <UDList style={{ overflow: 'unset', minHeight: 'calc(100vh - 314px)' }}>
        {entryRequestsByFilter.length > 0 ? (
          entryRequestsByFilter.map((entryRequest, index) => (
            <UDListItem
              key={entryRequest.id}
              index={index + 1}
              image={entryRequest.user.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
              title={getFullName(entryRequest.user)}
              subtitle={getBirthDay({
                birthDate: new Date(entryRequest.user.birthDate ?? ''),
              })}
              additionalFields={[
                <div key={entryRequest.id} className="flex-center" style={{ marginLeft: 'auto', paddingLeft: 10 }}>
                  <UDText type="subhead" color={theme.colors.SurfaceRomanSilver30}>
                    {t('entryRequest.status.CREATED')}
                  </UDText>
                </div>,
              ]}
            />
          ))
        ) : (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100%;
              min-height: calc(100vh - 314px);
            `}
          >
            <UDText
              color={theme.colors.SurfaceRomanSilver30}
              type="subhead"
              className="bold"
              style={{ fontWeight: 400, fontSize: 16 }}
            >
              {t('entryRequest.waitingList.empty')}
            </UDText>
          </div>
        )}
      </UDList>
    </ParticipantsList>
  );
};

export default WaitingList;
